import React, { memo, useMemo, useState } from 'react';
import { Popover, PopoverBody } from 'reactstrap';
import styled from 'styled-components';
import moment from 'moment';
import { FaCalendarAlt, FaChevronDown } from 'react-icons/fa';

const WEEK_DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const BRAND = '#6C5DD3';

const Container = styled.div`
    position: relative;
`;

const Trigger = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 5px 8px;
    cursor: pointer;
    background: #fff;
    font-size: 12px;
    color: #333;
    min-width: 80px;
    justify-content: space-between;
    &:hover { border-color: #aaa; }
`;

const TriggerLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;

const Divider = styled.span`
    width: 1px;
    height: 16px;
    background: #ccc;
    margin: 0 2px;
`;

const Cal = styled.div`
    width: 252px;
    user-select: none;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    font-weight: 700;
    font-size: 15px;
`;

const NavCol = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const NavBtn = styled.button`
    all: unset;
    cursor: pointer;
    font-size: 18px;
    color: #555;
    line-height: 1;
    &:hover { color: #000; }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    text-align: center;
    row-gap: 2px;
`;

const DayLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: #999;
    padding: 4px 0 6px;
`;

const Day = styled.div`
    padding: 5px 2px;
    font-size: 13px;
    cursor: ${p => (p.$other ? 'default' : 'pointer')};
    color: ${p => (p.$selected ? '#fff' : p.$other ? '#ccc' : '#222')};
    background: ${p => (p.$selected ? BRAND : 'transparent')};
    border-radius: 4px;
    &:hover {
        background: ${p =>
            p.$selected ? BRAND : p.$other ? 'transparent' : '#ede9ff'};
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #eee;
`;

const FBtn = styled.button`
    all: unset;
    color: ${BRAND};
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    &:hover { text-decoration: underline; }
`;

const DateFilter = ({ label, uniqueId, onChooseDate }) => {
    const [date, setDate] = useState(null);
    const [view, setView] = useState(() => moment());
    const [isOpen, setIsOpen] = useState(false);

    const safeId = `PopoverLegacy_${label.replace(/[^a-zA-Z0-9_-]/g, '_')}_${uniqueId}`;

    const days = useMemo(() => {
        const start = view.clone().startOf('month').startOf('week');
        const end = view.clone().endOf('month').endOf('week');
        const result = [];
        let d = start.clone();
        while (!d.isAfter(end, 'day')) {
            result.push(d.clone());
            d.add(1, 'day');
        }
        return result;
    }, [view]);

    const select = (d) => {
        setDate(d);
        onChooseDate(d);
        setIsOpen(false);
    };

    const clear = () => {
        setDate(null);
        onChooseDate(null);
        setIsOpen(false);
    };

    const goToday = () => {
        const t = moment();
        setDate(t);
        setView(t.clone());
        onChooseDate(t);
        setIsOpen(false);
    };

    return (
        <Container>
            <Trigger id={safeId} onClick={() => setIsOpen(o => !o)}>
                <TriggerLeft>
                    <FaCalendarAlt size={12} color={date ? BRAND : '#999'} />
                    {date && <span>{date.format('DD/MM/YY')}</span>}
                </TriggerLeft>
                <Divider />
                <FaChevronDown size={10} color="#999" />
            </Trigger>
            <Popover
                placement="bottom"
                isOpen={isOpen}
                target={safeId}
                toggle={() => setIsOpen(o => !o)}
            >
                <PopoverBody>
                    <Cal>
                        <Header>
                            <span>{view.format('MMM YYYY')} ▾</span>
                            <NavCol>
                                <NavBtn onClick={() => setView(v => v.clone().subtract(1, 'month'))}>↑</NavBtn>
                                <NavBtn onClick={() => setView(v => v.clone().add(1, 'month'))}>↓</NavBtn>
                            </NavCol>
                        </Header>
                        <Grid>
                            {WEEK_DAYS.map((d, i) => (
                                <DayLabel key={i}>{d}</DayLabel>
                            ))}
                            {days.map((d, i) => {
                                const isOther = !d.isSame(view, 'month');
                                const isSelected = date && d.isSame(date, 'day');
                                return (
                                    <Day
                                        key={i}
                                        $other={isOther}
                                        $selected={isSelected}
                                        onClick={() => !isOther && select(d)}
                                    >
                                        {d.date()}
                                    </Day>
                                );
                            })}
                        </Grid>
                        <Footer>
                            <FBtn onClick={clear}>Clear</FBtn>
                            <FBtn onClick={goToday}>Today</FBtn>
                        </Footer>
                    </Cal>
                </PopoverBody>
            </Popover>
        </Container>
    );
};

export default memo(DateFilter);
